import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import {
    AbstractControl,
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup, ValidationErrors, ValidatorFn,
    Validators
} from "@angular/forms";
import dayjs from 'dayjs';

import { finalize, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { AsyncPipe, NgClass, NgFor } from '@angular/common';
import {
    AddGiftCardToCartDocument,
    GetGiftCardTemplatesDocument,
    GetGiftCardTemplatesQuery,
    GetProductDetailQuery,
    ProductPreviewFragment,
} from '../../common/gql/graphql';
import { DataService } from '../../../core/providers/data/data.service';

import { NotificationService } from '../../../core/providers/notification/notification.service';
import { assertNever } from '../../../common/utils/assert-never';
import { ActiveService } from '../../../core/providers/active-order/active.service';
import { StateService } from '../../../core/providers/state.service';

import { OrderService } from '../../../core/providers/order/order.service';
import { StarRatingComponent } from '../../../shared/components/star-rating/star-rating.component';
import { TabbedDescriptionComponent } from '../tabbed-description/tabbed-description.component';
import { AssetGalleryComponent } from '../asset-gallery/asset-gallery.component';
import { ProductAccordionComponent } from '../product-accordion/product-accordion.component';
import { GiftCardMessageInputComponent } from "../gift-card-message-input/gift-card-message-input.component";

@Component({
    selector: 'kb-product-page-gift-card',
    templateUrl: './product-page-gift-card.component.html',
    styleUrls: ['./product-page-gift-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        AssetGalleryComponent,
        TabbedDescriptionComponent,
        StarRatingComponent,
        FormsModule,
        ReactiveFormsModule,
        NgFor,
        NgClass,
        AsyncPipe,
        ProductAccordionComponent,
        GiftCardMessageInputComponent
    ]
})
export class ProductPageGiftCardComponent implements OnInit {
    @Input() productPreview: ProductPreviewFragment;
    @Input() product: GetProductDetailQuery['product'] | null;
    giftCardForm: UntypedFormGroup;
    protected isEvoucher: boolean;
    protected isAdding = false;

    @ViewChild('addedToCartTemplate', { static: true })
    private addToCartTemplate: TemplateRef<any>;

    templates$: Observable<GetGiftCardTemplatesQuery['giftCardTemplates']['items']>;
    selectedTemplateId: string | undefined;
    readonly minDate = dayjs().format('YYYY-MM-DD');
    readonly maxDate = dayjs().add(3, 'month').format('YYYY-MM-DD');

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private dataService: DataService,
        private notificationService: NotificationService,
        private formBuilder: UntypedFormBuilder,
        private activeService: ActiveService,
        private stateService: StateService,
        private orderService: OrderService,
    ) {}

    ngOnInit() {
        this.isEvoucher = this.productPreview.name.toLowerCase().includes('evoucher');
        this.templates$ = this.dataService
            .query(GetGiftCardTemplatesDocument, {
                options: {
                    take: 10,
                    filter: {
                        configName: {
                            contains: this.isEvoucher ? 'evoucher' : 'physical',
                        },
                    },
                },
            })
            .pipe(
                map(({ giftCardTemplates }) => giftCardTemplates.items),
                tap((images) => (this.selectedTemplateId = images?.[0]?.id)),
            );
        this.giftCardForm = this.formBuilder.group({
            cardValue: 5,
            senderName: '',
            recipientEmail: ['', this.isEvoucher ? [Validators.required, Validators.email] : []],
            message: ['', maxLinesValidator(5)],
            deliverImmediately: [true],
            deliveryDate: [new Date()],
        });
        this.activeService.activeCustomer$.pipe(take(1)).subscribe((customer) => {
            if (customer) {
                this.giftCardForm.get('senderName')?.setValue(`${customer.firstName} ${customer.lastName}`);
            }
        });
    }

    roundAmount() {
        const formValue = this.giftCardForm.value;
        this.giftCardForm.get('cardValue')?.setValue(Math.round(formValue.cardValue));
    }

    addGiftCardToCart() {
        const formValue = this.giftCardForm.value;
        this.isAdding = true;
        this.dataService
            .mutate(AddGiftCardToCartDocument, {
                input: {
                    value: Math.round(formValue.cardValue) * 100,
                    senderName: formValue.senderName,
                    recipientEmailAddress: formValue.recipientEmail,
                    message: formValue.message,
                    templateId: this.selectedTemplateId as string,
                    deliveryDate: formValue.deliverImmediately
                        ? undefined
                        : dayjs(formValue.deliveryDate).toISOString(),
                },
            })
            .pipe(
                finalize(() => (this.isAdding = false)),
                switchMap(({ addGiftCardToOrder }) => {
                    switch (addGiftCardToOrder.__typename) {
                        case 'Order': {
                            this.stateService.setState('activeOrderId', addGiftCardToOrder.id);
                            this.notificationService
                                .notify({
                                    title: 'Added to cart',
                                    type: 'info',
                                    duration: 3000,
                                    templateRef: this.addToCartTemplate,
                                    templateContext: {
                                        quantity: 1,
                                    },
                                })
                                .subscribe();
                            return of(addGiftCardToOrder);
                        }
                        case 'OrderInterceptorError':
                        case 'NegativeQuantityError':
                        case 'OrderLimitError':
                        case 'OrderModificationError':
                        case 'InsufficientStockError':
                            this.notificationService.error(addGiftCardToOrder.message).subscribe();
                            break;
                        default:
                            assertNever(addGiftCardToOrder);
                    }
                    return of(null);
                }),
                switchMap((cart) => (cart ? this.orderService.ensureGiftCardShippingIsZero(cart) : of(null))),
            )
            .subscribe(() => {
                this.changeDetectorRef.markForCheck();
            });
    }
}

export function maxLinesValidator(maxLines: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const invalid = typeof control.value === 'string' && control.value.split('\n').length > maxLines;
        return invalid ? {tooManyRows: {value: control.value}} : null;
    };
}
